/**
 * VuePrint v1.6.3
 * Copyright (c) 2024 Jak Guru LLC
 * Vueprint a commerical work product released under the MIT License
 * and is provided as-is with no warranty or guarantee of support.
 * Documentation: https://jakguru.github.io/vueprint/
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { g as getDefaultExportFromCjs, m as merge } from './vueprint.22af4dba.mjs';
import { T as TinyEmitter } from './vueprint.b71ecad4.mjs';
import { ref, watch } from 'vue';
import { g as getDebugger } from './vueprint.b84f56b4.mjs';

function _process (v, mod) {
  var i;
  var r;

  if (typeof mod === 'function') {
    r = mod(v);
    if (r !== undefined) {
      v = r;
    }
  } else if (Array.isArray(mod)) {
    for (i = 0; i < mod.length; i++) {
      r = mod[i](v);
      if (r !== undefined) {
        v = r;
      }
    }
  }

  return v
}

function parseKey (key, val) {
  // detect negative index notation
  if (key[0] === '-' && Array.isArray(val) && /^-\d+$/.test(key)) {
    return val.length + parseInt(key, 10)
  }
  return key
}

function isIndex (k) {
  return /^\d+$/.test(k)
}

function isObject (val) {
  return Object.prototype.toString.call(val) === '[object Object]'
}

function isArrayOrObject (val) {
  return Object(val) === val
}

function isEmptyObject (val) {
  return Object.keys(val).length === 0
}

var blacklist = ['__proto__', 'prototype', 'constructor'];
var blacklistFilter = function (part) { return blacklist.indexOf(part) === -1 };

function parsePath (path, sep) {
  if (path.indexOf('[') >= 0) {
    path = path.replace(/\[/g, sep).replace(/]/g, '');
  }

  var parts = path.split(sep);

  var check = parts.filter(blacklistFilter);

  if (check.length !== parts.length) {
    throw Error('Refusing to update blacklisted property ' + path)
  }

  return parts
}

var hasOwnProperty = Object.prototype.hasOwnProperty;

function DotObject (separator, override, useArray, useBrackets) {
  if (!(this instanceof DotObject)) {
    return new DotObject(separator, override, useArray, useBrackets)
  }

  if (typeof override === 'undefined') override = false;
  if (typeof useArray === 'undefined') useArray = true;
  if (typeof useBrackets === 'undefined') useBrackets = true;
  this.separator = separator || '.';
  this.override = override;
  this.useArray = useArray;
  this.useBrackets = useBrackets;
  this.keepArray = false;

  // contains touched arrays
  this.cleanup = [];
}

var dotDefault = new DotObject('.', false, true, true);
function wrap (method) {
  return function () {
    return dotDefault[method].apply(dotDefault, arguments)
  }
}

DotObject.prototype._fill = function (a, obj, v, mod) {
  var k = a.shift();

  if (a.length > 0) {
    obj[k] = obj[k] || (this.useArray && isIndex(a[0]) ? [] : {});

    if (!isArrayOrObject(obj[k])) {
      if (this.override) {
        obj[k] = {};
      } else {
        if (!(isArrayOrObject(v) && isEmptyObject(v))) {
          throw new Error(
            'Trying to redefine `' + k + '` which is a ' + typeof obj[k]
          )
        }

        return
      }
    }

    this._fill(a, obj[k], v, mod);
  } else {
    if (!this.override && isArrayOrObject(obj[k]) && !isEmptyObject(obj[k])) {
      if (!(isArrayOrObject(v) && isEmptyObject(v))) {
        throw new Error("Trying to redefine non-empty obj['" + k + "']")
      }

      return
    }

    obj[k] = _process(v, mod);
  }
};

/**
 *
 * Converts an object with dotted-key/value pairs to it's expanded version
 *
 * Optionally transformed by a set of modifiers.
 *
 * Usage:
 *
 *   var row = {
 *     'nr': 200,
 *     'doc.name': '  My Document  '
 *   }
 *
 *   var mods = {
 *     'doc.name': [_s.trim, _s.underscored]
 *   }
 *
 *   dot.object(row, mods)
 *
 * @param {Object} obj
 * @param {Object} mods
 */
DotObject.prototype.object = function (obj, mods) {
  var self = this;

  Object.keys(obj).forEach(function (k) {
    var mod = mods === undefined ? null : mods[k];
    // normalize array notation.
    var ok = parsePath(k, self.separator).join(self.separator);

    if (ok.indexOf(self.separator) !== -1) {
      self._fill(ok.split(self.separator), obj, obj[k], mod);
      delete obj[k];
    } else {
      obj[k] = _process(obj[k], mod);
    }
  });

  return obj
};

/**
 * @param {String} path dotted path
 * @param {String} v value to be set
 * @param {Object} obj object to be modified
 * @param {Function|Array} mod optional modifier
 */
DotObject.prototype.str = function (path, v, obj, mod) {
  var ok = parsePath(path, this.separator).join(this.separator);

  if (path.indexOf(this.separator) !== -1) {
    this._fill(ok.split(this.separator), obj, v, mod);
  } else {
    obj[path] = _process(v, mod);
  }

  return obj
};

/**
 *
 * Pick a value from an object using dot notation.
 *
 * Optionally remove the value
 *
 * @param {String} path
 * @param {Object} obj
 * @param {Boolean} remove
 */
DotObject.prototype.pick = function (path, obj, remove, reindexArray) {
  var i;
  var keys;
  var val;
  var key;
  var cp;

  keys = parsePath(path, this.separator);
  for (i = 0; i < keys.length; i++) {
    key = parseKey(keys[i], obj);
    if (obj && typeof obj === 'object' && key in obj) {
      if (i === keys.length - 1) {
        if (remove) {
          val = obj[key];
          if (reindexArray && Array.isArray(obj)) {
            obj.splice(key, 1);
          } else {
            delete obj[key];
          }
          if (Array.isArray(obj)) {
            cp = keys.slice(0, -1).join('.');
            if (this.cleanup.indexOf(cp) === -1) {
              this.cleanup.push(cp);
            }
          }
          return val
        } else {
          return obj[key]
        }
      } else {
        obj = obj[key];
      }
    } else {
      return undefined
    }
  }
  if (remove && Array.isArray(obj)) {
    obj = obj.filter(function (n) {
      return n !== undefined
    });
  }
  return obj
};
/**
 *
 * Delete value from an object using dot notation.
 *
 * @param {String} path
 * @param {Object} obj
 * @return {any} The removed value
 */
DotObject.prototype.delete = function (path, obj) {
  return this.remove(path, obj, true)
};

/**
 *
 * Remove value from an object using dot notation.
 *
 * Will remove multiple items if path is an array.
 * In this case array indexes will be retained until all
 * removals have been processed.
 *
 * Use dot.delete() to automatically  re-index arrays.
 *
 * @param {String|Array<String>} path
 * @param {Object} obj
 * @param {Boolean} reindexArray
 * @return {any} The removed value
 */
DotObject.prototype.remove = function (path, obj, reindexArray) {
  var i;

  this.cleanup = [];
  if (Array.isArray(path)) {
    for (i = 0; i < path.length; i++) {
      this.pick(path[i], obj, true, reindexArray);
    }
    if (!reindexArray) {
      this._cleanup(obj);
    }
    return obj
  } else {
    return this.pick(path, obj, true, reindexArray)
  }
};

DotObject.prototype._cleanup = function (obj) {
  var ret;
  var i;
  var keys;
  var root;
  if (this.cleanup.length) {
    for (i = 0; i < this.cleanup.length; i++) {
      keys = this.cleanup[i].split('.');
      root = keys.splice(0, -1).join('.');
      ret = root ? this.pick(root, obj) : obj;
      ret = ret[keys[0]].filter(function (v) {
        return v !== undefined
      });
      this.set(this.cleanup[i], ret, obj);
    }
    this.cleanup = [];
  }
};

/**
 * Alias method  for `dot.remove`
 *
 * Note: this is not an alias for dot.delete()
 *
 * @param {String|Array<String>} path
 * @param {Object} obj
 * @param {Boolean} reindexArray
 * @return {any} The removed value
 */
DotObject.prototype.del = DotObject.prototype.remove;

/**
 *
 * Move a property from one place to the other.
 *
 * If the source path does not exist (undefined)
 * the target property will not be set.
 *
 * @param {String} source
 * @param {String} target
 * @param {Object} obj
 * @param {Function|Array} mods
 * @param {Boolean} merge
 */
DotObject.prototype.move = function (source, target, obj, mods, merge) {
  if (typeof mods === 'function' || Array.isArray(mods)) {
    this.set(target, _process(this.pick(source, obj, true), mods), obj, merge);
  } else {
    merge = mods;
    this.set(target, this.pick(source, obj, true), obj, merge);
  }

  return obj
};

/**
 *
 * Transfer a property from one object to another object.
 *
 * If the source path does not exist (undefined)
 * the property on the other object will not be set.
 *
 * @param {String} source
 * @param {String} target
 * @param {Object} obj1
 * @param {Object} obj2
 * @param {Function|Array} mods
 * @param {Boolean} merge
 */
DotObject.prototype.transfer = function (
  source,
  target,
  obj1,
  obj2,
  mods,
  merge
) {
  if (typeof mods === 'function' || Array.isArray(mods)) {
    this.set(
      target,
      _process(this.pick(source, obj1, true), mods),
      obj2,
      merge
    );
  } else {
    merge = mods;
    this.set(target, this.pick(source, obj1, true), obj2, merge);
  }

  return obj2
};

/**
 *
 * Copy a property from one object to another object.
 *
 * If the source path does not exist (undefined)
 * the property on the other object will not be set.
 *
 * @param {String} source
 * @param {String} target
 * @param {Object} obj1
 * @param {Object} obj2
 * @param {Function|Array} mods
 * @param {Boolean} merge
 */
DotObject.prototype.copy = function (source, target, obj1, obj2, mods, merge) {
  if (typeof mods === 'function' || Array.isArray(mods)) {
    this.set(
      target,
      _process(
        // clone what is picked
        JSON.parse(JSON.stringify(this.pick(source, obj1, false))),
        mods
      ),
      obj2,
      merge
    );
  } else {
    merge = mods;
    this.set(target, this.pick(source, obj1, false), obj2, merge);
  }

  return obj2
};

/**
 *
 * Set a property on an object using dot notation.
 *
 * @param {String} path
 * @param {any} val
 * @param {Object} obj
 * @param {Boolean} merge
 */
DotObject.prototype.set = function (path, val, obj, merge) {
  var i;
  var k;
  var keys;
  var key;

  // Do not operate if the value is undefined.
  if (typeof val === 'undefined') {
    return obj
  }
  keys = parsePath(path, this.separator);

  for (i = 0; i < keys.length; i++) {
    key = keys[i];
    if (i === keys.length - 1) {
      if (merge && isObject(val) && isObject(obj[key])) {
        for (k in val) {
          if (hasOwnProperty.call(val, k)) {
            obj[key][k] = val[k];
          }
        }
      } else if (merge && Array.isArray(obj[key]) && Array.isArray(val)) {
        for (var j = 0; j < val.length; j++) {
          obj[keys[i]].push(val[j]);
        }
      } else {
        obj[key] = val;
      }
    } else if (
      // force the value to be an object
      !hasOwnProperty.call(obj, key) ||
      (!isObject(obj[key]) && !Array.isArray(obj[key]))
    ) {
      // initialize as array if next key is numeric
      if (/^\d+$/.test(keys[i + 1])) {
        obj[key] = [];
      } else {
        obj[key] = {};
      }
    }
    obj = obj[key];
  }
  return obj
};

/**
 *
 * Transform an object
 *
 * Usage:
 *
 *   var obj = {
 *     "id": 1,
 *    "some": {
 *      "thing": "else"
 *    }
 *   }
 *
 *   var transform = {
 *     "id": "nr",
 *    "some.thing": "name"
 *   }
 *
 *   var tgt = dot.transform(transform, obj)
 *
 * @param {Object} recipe Transform recipe
 * @param {Object} obj Object to be transformed
 * @param {Array} mods modifiers for the target
 */
DotObject.prototype.transform = function (recipe, obj, tgt) {
  obj = obj || {};
  tgt = tgt || {};
  Object.keys(recipe).forEach(
    function (key) {
      this.set(recipe[key], this.pick(key, obj), tgt);
    }.bind(this)
  );
  return tgt
};

/**
 *
 * Convert object to dotted-key/value pair
 *
 * Usage:
 *
 *   var tgt = dot.dot(obj)
 *
 *   or
 *
 *   var tgt = {}
 *   dot.dot(obj, tgt)
 *
 * @param {Object} obj source object
 * @param {Object} tgt target object
 * @param {Array} path path array (internal)
 */
DotObject.prototype.dot = function (obj, tgt, path) {
  tgt = tgt || {};
  path = path || [];
  var isArray = Array.isArray(obj);

  Object.keys(obj).forEach(
    function (key) {
      var index = isArray && this.useBrackets ? '[' + key + ']' : key;
      if (
        isArrayOrObject(obj[key]) &&
        ((isObject(obj[key]) && !isEmptyObject(obj[key])) ||
          (Array.isArray(obj[key]) && !this.keepArray && obj[key].length !== 0))
      ) {
        if (isArray && this.useBrackets) {
          var previousKey = path[path.length - 1] || '';
          return this.dot(
            obj[key],
            tgt,
            path.slice(0, -1).concat(previousKey + index)
          )
        } else {
          return this.dot(obj[key], tgt, path.concat(index))
        }
      } else {
        if (isArray && this.useBrackets) {
          tgt[path.join(this.separator).concat('[' + key + ']')] = obj[key];
        } else {
          tgt[path.concat(index).join(this.separator)] = obj[key];
        }
      }
    }.bind(this)
  );
  return tgt
};

DotObject.pick = wrap('pick');
DotObject.move = wrap('move');
DotObject.transfer = wrap('transfer');
DotObject.transform = wrap('transform');
DotObject.copy = wrap('copy');
DotObject.object = wrap('object');
DotObject.str = wrap('str');
DotObject.set = wrap('set');
DotObject.delete = wrap('delete');
DotObject.del = DotObject.remove = wrap('remove');
DotObject.dot = wrap('dot');
['override', 'overwrite'].forEach(function (prop) {
  Object.defineProperty(DotObject, prop, {
    get: function () {
      return dotDefault.override
    },
    set: function (val) {
      dotDefault.override = !!val;
    }
  });
});
['useArray', 'keepArray', 'useBrackets'].forEach(function (prop) {
  Object.defineProperty(DotObject, prop, {
    get: function () {
      return dotDefault[prop]
    },
    set: function (val) {
      dotDefault[prop] = val;
    }
  });
});

DotObject._process = _process;

var dotObject = DotObject;

const dot = /*@__PURE__*/getDefaultExportFromCjs(dotObject);

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _namespace, _bus, _instance, _ref, _promise, _loaded, _unwatch, _inServerEnvironment, _channel, _doRefresh, doRefresh_fn, _commit, commit_fn;
const debug = getDebugger("LocalStorage");
class LocalStorageService {
  /**
   * Create a new LocalStorage instance.
   * @param namespace The namespace to use for the local storage.
   */
  constructor(namespace) {
    __privateAdd(this, _doRefresh);
    __privateAdd(this, _commit);
    __privateAdd(this, _namespace, void 0);
    __privateAdd(this, _bus, void 0);
    __privateAdd(this, _instance, void 0);
    __privateAdd(this, _ref, void 0);
    __privateAdd(this, _promise, void 0);
    __privateAdd(this, _loaded, false);
    __privateAdd(this, _unwatch, void 0);
    __privateAdd(this, _inServerEnvironment, void 0);
    __privateAdd(this, _channel, void 0);
    __privateSet(this, _namespace, namespace);
    __privateSet(this, _inServerEnvironment, !(typeof window !== "undefined"));
    __privateSet(this, _bus, new TinyEmitter());
    __privateSet(this, _ref, ref({}));
    if (typeof window !== "undefined") {
      __privateSet(this, _channel, new BroadcastChannel(`ls:${namespace}`));
      __privateGet(this, _channel).onmessage = () => {
        debug("Updating due to change in another tab.");
        this.refresh();
      };
      this.on("change", () => {
        debug("Notifying other tabs about change.");
        __privateGet(this, _channel)?.postMessage("change");
      });
      __privateSet(this, _promise, new Promise((resolve) => {
        import('../../chunks/secure-ls.mjs').then(function (n) { return n.s; }).then(({ default: SecureLS }) => {
          __privateSet(this, _instance, new SecureLS({
            encodingType: "aes",
            isCompression: true,
            encryptionSecret: namespace
          }));
          __privateMethod(this, _doRefresh, doRefresh_fn).call(this);
          __privateGet(this, _bus).on("tab:active", __privateMethod(this, _doRefresh, doRefresh_fn).bind(this), { local: true });
          __privateSet(this, _unwatch, watch(
            () => __privateGet(this, _ref).value,
            () => {
              __privateGet(this, _bus).emit("change");
              __privateMethod(this, _commit, commit_fn).call(this);
            },
            {
              deep: true
            }
          ));
          debug(`Loaded Encrypted Local Storage Values for ${namespace}.`);
          __privateSet(this, _loaded, true);
          __privateGet(this, _bus).emit("loaded");
          resolve();
        });
      }));
    } else {
      __privateSet(this, _promise, Promise.resolve());
    }
    __privateGet(this, _promise).then(() => {
      debug(`Loaded Encrypted Local Storage for ${namespace}.`);
    });
    this.on("loaded", () => {
      if (window) {
        if (!window._vueprint_loaded) {
          window._vueprint_loaded = {};
        }
        window._vueprint_loaded.localstorage = true;
        const event = new CustomEvent("localstorage:loaded", { detail: {} });
        window.dispatchEvent(event);
      }
    });
    __privateSet(this, _unwatch, () => {
    });
  }
  /**
   * Whether or not the local storage has been loaded.
   */
  get loaded() {
    return __privateGet(this, _loaded);
  }
  /**
   * A promise which resolves when the local storage has been loaded.
   */
  get promise() {
    return __privateGet(this, _promise);
  }
  /**
   * The current content of the local storage.
   */
  get value() {
    return __privateGet(this, _ref).value;
  }
  /**
   * Refresh the information in the service from the browser's local storage.
   */
  refresh() {
    if (__privateGet(this, _unwatch)) {
      __privateGet(this, _unwatch).call(this);
    }
    __privateMethod(this, _doRefresh, doRefresh_fn).call(this);
    __privateSet(this, _unwatch, watch(
      () => __privateGet(this, _ref).value,
      () => {
        __privateGet(this, _bus).emit("change");
        __privateMethod(this, _commit, commit_fn).call(this);
      },
      {
        deep: true
      }
    ));
  }
  /**
   * Get the encryption secret used by the local storage.
   */
  getEncryptionSecret() {
    if (!__privateGet(this, _instance) && !__privateGet(this, _inServerEnvironment)) {
      debug(
        `Trying to fetch encryption secret from localStorage ${__privateGet(this, _namespace)} before it is loaded.`
      );
    }
    return __privateGet(this, _instance)?.getEncryptionSecret() || "";
  }
  /**
   * Get the value of a key in the local storage.
   * @param key The key to fetch from the local storage.
   * @returns The value of the key in the local storage, if it exists
   */
  get(key) {
    if (!__privateGet(this, _instance) && !__privateGet(this, _inServerEnvironment)) {
      debug(`Trying to fetch ${key} from localStorage ${__privateGet(this, _namespace)} before it is loaded.`);
    }
    return dot.pick(key, __privateGet(this, _ref).value);
  }
  /**
   * Get the value of a key in the local storage.
   * @param key The key to fetch from the local storage.
   * @returns The value of the key in the local storage, if it exists
   */
  getDataFromLocalStorage(key) {
    if (!__privateGet(this, _instance) && !__privateGet(this, _inServerEnvironment)) {
      debug(`Trying to fetch ${key} from localStorage ${__privateGet(this, _namespace)} before it is loaded.`);
    }
    return __privateGet(this, _instance)?.getDataFromLocalStorage(key) || null;
  }
  /**
   * Get all of the keys in the local storage.
   * @returns An array of all of the keys in the local storage.
   */
  getAllKeys() {
    if (!__privateGet(this, _instance) && !__privateGet(this, _inServerEnvironment)) {
      debug(`Trying to fetch keys from localStorage ${__privateGet(this, _namespace)} before it is loaded.`);
    }
    return Object.keys(__privateGet(this, _ref).value);
  }
  /**
   * Set the value of a key in the local storage.
   * @param key The key to set in the local storage.
   * @param data The value to set for the key in the local storage.
   */
  set(key, data) {
    if (!__privateGet(this, _instance) && !__privateGet(this, _inServerEnvironment)) {
      debug(`Trying to set ${key} in localStorage ${__privateGet(this, _namespace)} before it is loaded.`);
    }
    if (__privateGet(this, _unwatch)) {
      __privateGet(this, _unwatch).call(this);
    }
    dot.set(key, data, __privateGet(this, _ref).value);
    __privateMethod(this, _commit, commit_fn).call(this);
    __privateSet(this, _unwatch, watch(
      () => __privateGet(this, _ref).value,
      () => {
        __privateGet(this, _bus).emit("change");
        __privateMethod(this, _commit, commit_fn).call(this);
      },
      {
        deep: true
      }
    ));
    __privateGet(this, _bus).emit("change");
  }
  /**
   * Merge data into the local storage.
   * @param data The data to merge into the local storage.
   */
  merge(data) {
    const src = { ...__privateGet(this, _ref).value };
    const dst = { ...data };
    const merged = merge({}, src, dst);
    __privateGet(this, _ref).value = merged;
    __privateMethod(this, _commit, commit_fn).call(this);
  }
  /**
   * Set the value of a key in the local storage.
   * @param key The key to set in the local storage.
   * @param data The value to set for the key in the local storage.
   */
  setDataToLocalStorage(key, data) {
    if (__privateGet(this, _instance)) {
      __privateGet(this, _bus).emit("setDataToLocalStorage", key, data);
    }
    return __privateGet(this, _instance)?.setDataToLocalStorage(key, data);
  }
  /**
   * Remove a key from the local storage.
   * @param key The key to remove from the local storage.
   */
  remove(key) {
    if (!__privateGet(this, _instance) && !__privateGet(this, _inServerEnvironment)) {
      debug(`Trying to remove ${key} from localStorage ${__privateGet(this, _namespace)} before it is loaded.`);
    }
    dot.delete(key, __privateGet(this, _ref).value);
    __privateMethod(this, _commit, commit_fn).call(this);
  }
  /**
   * Remove all keys from the local storage.
   */
  removeAll() {
    if (!__privateGet(this, _instance) && !__privateGet(this, _inServerEnvironment)) {
      debug(`Trying to remove all keys from localStorage ${__privateGet(this, _namespace)} before it is loaded.`);
    }
    __privateGet(this, _ref).value = {};
    __privateMethod(this, _commit, commit_fn).call(this);
  }
  /**
   * Clear the local storage.
   */
  clear() {
    this.removeAll();
    if (__privateGet(this, _instance)) {
      __privateGet(this, _bus).emit("clear");
    } else {
      debug(`Trying to clear localStorage ${__privateGet(this, _namespace)} before it is loaded.`);
    }
    return __privateGet(this, _instance)?.clear();
  }
  /**
   * Reset all keys in the local storage.
   */
  resetAllKeys() {
    if (__privateGet(this, _instance)) {
      __privateGet(this, _bus).emit("resetAllKeys");
    }
    return __privateGet(this, _instance)?.resetAllKeys() || [];
  }
  /**
   * Add a listener for an event on the local bus.
   * @param event The event name.
   * @param callback The callback function.
   */
  on(event, callback) {
    __privateGet(this, _bus).on(event, callback);
  }
  /**
   * Remove a listener for an event on the local bus.
   * @param event The event name.
   * @param callback The callback function.
   */
  off(event, callback) {
    __privateGet(this, _bus).off(event, callback);
  }
  /**
   * Add a listener for an event on the local bus one time
   * @param event The event name.
   * @param callback The callback function.
   */
  once(event, callback) {
    __privateGet(this, _bus).once(event, callback);
  }
}
_namespace = new WeakMap();
_bus = new WeakMap();
_instance = new WeakMap();
_ref = new WeakMap();
_promise = new WeakMap();
_loaded = new WeakMap();
_unwatch = new WeakMap();
_inServerEnvironment = new WeakMap();
_channel = new WeakMap();
_doRefresh = new WeakSet();
doRefresh_fn = function() {
  if (__privateGet(this, _instance)) {
    let current;
    try {
      current = JSON.parse(__privateGet(this, _instance).get(__privateGet(this, _namespace)) || "{}");
    } catch (error) {
      debug(`Error parsing localStorage ${__privateGet(this, _namespace)}.`, error);
      current = {};
    }
    const merged = merge({}, __privateGet(this, _ref).value, current);
    __privateGet(this, _ref).value = merged;
  } else if (!__privateGet(this, _inServerEnvironment)) {
    debug(`Trying to refresh localStorage ${__privateGet(this, _namespace)} before it is loaded.`);
  }
};
_commit = new WeakSet();
commit_fn = function() {
  if (__privateGet(this, _instance)) {
    __privateGet(this, _instance).set(__privateGet(this, _namespace), JSON.stringify({ ...__privateGet(this, _ref).value }));
  }
};

const localStorage = {
  __proto__: null,
  LocalStorageService: LocalStorageService
};

export { LocalStorageService as L, localStorage as l };
//# sourceMappingURL=vueprint.19cdf2cc.mjs.map
